import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { attribution } from "@/lib/attribution";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import PhoneVerify from "@/components/PhoneVerify";
import SavedAddresses from "@/components/SavedAddresses";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Truck, BadgeCheck, MessageCircle, RefreshCw, AlertTriangle, Lock, Minus, Plus, Tag, Trash2 } from "lucide-react";
import { openRazorpayCheckout } from "@/lib/razorpay";

const FORM_KEY = "ayur_checkout_form";

const savedForm = () => {
  try {
    return JSON.parse(localStorage.getItem(FORM_KEY) || "null");
  } catch {
    return null;
  }
};

export default function Checkout() {
  const { items, total: subtotal, setQty, remove: removeItem, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState({ whatsapp_number: "", gst_rate: 0.05 });
  // Kept in localStorage as it is typed, so a refresh or a dropped connection does
  // not cost the buyer their details. Cleared only once the order is placed.
  const [form, setForm] = useState(() => ({
    customer_name: user?.name || "",
    customer_email: user?.email || "",
    customer_phone: user?.phone || "",
    address: user?.address || "",
    ...(savedForm() || {}),
  }));
  const [phoneToken, setPhoneToken] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [offer, setOffer] = useState(null); // { code, label, discount }
  const [offers, setOffers] = useState([]); // every live promo, for the pick-an-offer list
  const [stock, setStock] = useState({});
  const [checkingCode, setCheckingCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null); // { orderId, reason } after a failed/abandoned payment

  useEffect(() => { api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/promo/list").then(({ data }) => setOffers(data.promos || [])).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/products")
      .then(({ data }) => setStock(Object.fromEntries(data.map((p) => [p.id, p.stock]))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(FORM_KEY, JSON.stringify(form));
    } catch (e) {
      console.error("checkout form persist failed:", e);
    }
  }, [form]);

  const finishOrder = () => {
    localStorage.removeItem(FORM_KEY);
    clear();
  };

  const gstRate = settings.gst_rate || 0.05;
  const discount = offer ? offer.discount : 0;
  const taxable = Math.round((subtotal - discount) * 100) / 100;
  const taxAmount = Math.round(taxable * gstRate * 100) / 100;
  const grandTotal = Math.round((taxable + taxAmount) * 100) / 100;
  // What the same cart would cost with no offer, so the saving can be shown honestly.
  const fullTotal = Math.round(subtotal * (1 + gstRate) * 100) / 100;
  const saved = Math.round((fullTotal - grandTotal) * 100) / 100;
  // Quantities are fixed once an order exists at the gateway — its amount is already set.
  const locked = busy || Boolean(failed);

  // A changed cart changes the discount, so the applied offer is re-priced by the
  // server rather than trusted from the earlier total.
  useEffect(() => {
    if (!offer) return undefined;
    let alive = true;
    api.post("/coupons/validate", { code: offer.code, subtotal })
      .then(({ data }) => { if (alive && data.discount !== offer.discount) setOffer(data); })
      .catch((e) => {
        if (!alive) return;
        setOffer(null);
        toast.info(formatApiError(e.response?.data?.detail));
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const applyCode = async (raw) => {
    const code = (typeof raw === "string" ? raw : codeInput).trim();
    if (!code) return;
    setCheckingCode(true);
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal });
      setOffer(data);
      setCodeInput(data.code);
      toast.success(`${data.code} applied — ₹${data.discount.toFixed(2)} off`);
    } catch (e) {
      setOffer(null);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setCheckingCode(false); }
  };

  const removeCode = () => { setOffer(null); setCodeInput(""); };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onPhoneChange = (e) => { setForm({ ...form, customer_phone: e.target.value }); setPhoneToken(""); };

  const validate = () => {
    if (!form.customer_name.trim()) return "Please enter your full name";
    if (!/^\S+@\S+\.\S+$/.test(form.customer_email)) return "Please enter a valid email";
    if (!/^[0-9+\-\s()]{7,}$/.test(form.customer_phone)) return "Please enter a valid phone number";
    if (!form.address.trim()) return "Please enter a delivery address";
    if (items.length === 0) return "Your cart is empty";
    if (!user && !phoneToken) return "Please verify your phone with OTP";
    return null;
  };

  const startPayment = async (order) => {
    await openRazorpayCheckout({
      order,
      prefill: { name: form.customer_name, email: form.customer_email, contact: form.customer_phone },
      onSuccess: async (resp) => {
        try {
          await api.post("/orders/verify-payment", {
            order_id: order.id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_signature: resp.razorpay_signature,
          });
          setFailed(null);
          toast.success("Payment successful! Your remedies are on their way.");
          finishOrder();
          nav("/order-success", { state: { order, phoneToken } });
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail));
        }
      },
      onFailure: (reason) => {
        setFailed({ orderId: order.id, reason });
        toast.error(reason);
      },
      onDismiss: () => {
        setFailed({ orderId: order.id, reason: "You closed the payment window before finishing." });
        toast.info("Payment cancelled — your cart is still saved");
      },
    });
  };

  const pay = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const { data: order } = await api.post("/orders/checkout", {
        items: items.map(({ id, quantity }) => ({ product_id: id, quantity })),
        ...form,
        phone_token: phoneToken,
        coupon_code: offer ? offer.code : "",
        ...attribution(),
      });

      if (order.mock_payment || !order.razorpay_order_id || !order.razorpay_key_id) {
        await api.post(`/orders/${order.id}/mock-pay`);
        toast.success("Order placed! We'll contact you on the phone number provided.");
        finishOrder();
        nav("/order-success", { state: { order, phoneToken } });
        return;
      }

      await startPayment(order);
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      // The server is the authority on offers. If it turned this code down, drop it
      // from the cart so the buyer can pay the correct amount straight away.
      if (/promo code/i.test(detail)) {
        setOffer(null);
        setCodeInput("");
      }
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const retryPayment = async () => {
    if (!failed) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${failed.orderId}/retry-payment`, { phone_token: phoneToken });
      if (data.dropped_discount_reason) {
        setOffer(null);
        setCodeInput("");
        toast.info(`${data.dropped_discount_reason} — your order is now ₹${Number(data.total).toFixed(2)}`);
      }
      await startPayment({ ...data, id: failed.orderId });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const retryOnWhatsapp = () => {
    const num = (settings.whatsapp_number || "").replace(/\D/g, "");
    if (!num) return toast.error("WhatsApp number not configured. Contact us via phone.");
    const lines = items.map((it) => `• ${it.name} × ${it.quantity} — ₹${(it.price * it.quantity).toFixed(2)}`).join("\n");
    const msg =
      `Namaste! My payment for order #${failed?.orderId?.slice(0, 8)} did not go through on the website.\n\n` +
      `${lines}\n\nTotal: ₹${grandTotal.toFixed(2)}\n\n` +
      `Name: ${form.customer_name || "-"}\nPhone: ${form.customer_phone || "-"}\nAddress: ${form.address || "-"}\n\n` +
      `Please help me complete this order.`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const whatsappOrder = () => {
    const num = (settings.whatsapp_number || "").replace(/\D/g, "");
    if (!num) return toast.error("WhatsApp number not configured. Contact us via phone.");
    const lines = items.map((it) => `• ${it.name} × ${it.quantity} — ₹${(it.price * it.quantity).toFixed(2)}\n  Link: ${window.location.origin}/shop/${it.id}`).join("\n");
    const msg =
      `Namaste! I would like to order the following from Immortal Vedas:\n\n${lines}\n\n` +
      `Subtotal: ₹${subtotal.toFixed(2)}\n` +
      (offer ? `Offer ${offer.code}: − ₹${discount.toFixed(2)}\n` : "") +
      `GST (${(gstRate*100).toFixed(0)}%): ₹${taxAmount.toFixed(2)}\nTotal: ₹${grandTotal.toFixed(2)}\n\n` +
      `Name: ${form.customer_name || "-"}\nPhone: ${form.customer_phone || "-"}\nAddress: ${form.address || "-"}`;
    const url = `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  return (
    <main className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
      <div className="overline mb-2">Secure Checkout</div>
      <h1 className="font-display text-4xl lg:text-5xl mb-2">Complete Your Order</h1>
      <p className="text-muted-foreground mb-10 text-sm">No account required — just verify your phone with OTP so we can reach you.</p>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-5" data-testid="checkout-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="overline">Full Name *</Label>
              <Input data-testid="checkout-name" value={form.customer_name} onChange={update("customer_name")}
                placeholder="As on the delivery address" className="mt-2 bg-transparent h-11" />
            </div>
            <div>
              <Label className="overline">Phone *</Label>
              <Input data-testid="checkout-phone" type="tel" value={form.customer_phone} onChange={onPhoneChange}
                placeholder="+91 98xxxxxxxx" className="mt-2 bg-transparent h-11" />
            </div>
          </div>
          <div>
            <Label className="overline">Email *</Label>
            <Input data-testid="checkout-email" type="email" value={form.customer_email} onChange={update("customer_email")}
              placeholder="you@example.com" className="mt-2 bg-transparent h-11" />
            <p className="text-[11px] text-muted-foreground mt-1">We'll send your invoice and order updates here.</p>
          </div>
          <div>
            <Label className="overline">Delivery Address *</Label>
            <Textarea data-testid="checkout-address" rows={4} value={form.address} onChange={update("address")}
              placeholder="House no, street, city, state, PIN" className="mt-2 bg-transparent" />
          </div>

          {user && (
            <SavedAddresses
              currentAddress={form.address}
              disabled={locked}
              onPick={(a) => {
                setForm((f) => ({
                  ...f,
                  address: a.address,
                  customer_name: a.name || f.customer_name,
                  customer_phone: a.phone || f.customer_phone,
                }));
                toast.success("Address filled in");
              }}
            />
          )}

          {!user && (
            <PhoneVerify phone={form.customer_phone} verified={Boolean(phoneToken)} onVerified={setPhoneToken}/>
          )}

          <div className="border border-border rounded-xl p-4 bg-card">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Your Items</div>
              {locked && (
                <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5" data-testid="checkout-qty-locked">
                  <Lock size={11} /> Quantities locked while this payment is being completed
                </span>
              )}
            </div>
            {items.map((it) => {
              const cap = stock[it.id];
              const atCap = typeof cap === "number" && it.quantity >= cap;
              return (
                <div key={it.id} className="flex items-center gap-3 py-2.5 border-t border-border/60 first:border-0" data-testid={`checkout-item-${it.id}`}>
                  <div className="w-12 h-12 rounded-lg bg-muted overflow-hidden shrink-0">
                    {it.image && <img src={mediaUrl(it.image)} alt={it.name} className="w-full h-full object-cover"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{it.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      ₹{it.price.toFixed(2)} each{atCap ? ` · only ${cap} available` : ""}
                    </div>
                  </div>
                  <div className="flex items-center border border-border rounded-full">
                    <button
                      onClick={() => setQty(it.id, it.quantity - 1)}
                      disabled={locked || it.quantity <= 1}
                      className="px-2.5 py-1.5 text-muted-foreground hover:text-primary disabled:opacity-30"
                      aria-label="Reduce quantity"
                      data-testid={`checkout-dec-${it.id}`}
                    >
                      <Minus size={12} />
                    </button>
                    <span className="w-7 text-center text-sm" data-testid={`checkout-qty-${it.id}`}>{it.quantity}</span>
                    <button
                      onClick={() => setQty(it.id, it.quantity + 1)}
                      disabled={locked || atCap}
                      className="px-2.5 py-1.5 text-muted-foreground hover:text-primary disabled:opacity-30"
                      aria-label="Increase quantity"
                      data-testid={`checkout-inc-${it.id}`}
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                  <span className="text-sm w-20 text-right" data-testid={`checkout-line-${it.id}`}>₹{(it.price * it.quantity).toFixed(2)}</span>
                  <button
                    onClick={() => removeItem(it.id)}
                    disabled={locked}
                    className="text-muted-foreground hover:text-destructive disabled:opacity-30"
                    aria-label="Remove item"
                    data-testid={`checkout-rm-${it.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
            {items.length === 0 && (
              <p className="text-sm text-muted-foreground py-2" data-testid="checkout-empty">
                Your cart is empty — add a remedy from the shop to continue.
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-5 text-xs text-muted-foreground pt-2">
            <span className="flex items-center gap-1"><ShieldCheck size={14} className="text-primary" /> Secure payment</span>
            <span className="flex items-center gap-1"><Truck size={14} className="text-primary" /> Free shipping</span>
            <span className="flex items-center gap-1"><BadgeCheck size={14} className="text-primary" /> Classical formulations</span>
          </div>
        </div>

        <div className="border border-border rounded-2xl p-6 h-fit bg-card">
          <h3 className="font-display text-2xl mb-4">Summary</h3>
          <div className="flex justify-between text-sm mb-2"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
          {offer && (
            <div className="flex justify-between text-sm mb-2 text-primary" data-testid="checkout-discount-row">
              <span>{offer.label || offer.code}</span><span>− ₹{discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm mb-2"><span>GST ({(gstRate*100).toFixed(0)}%)</span><span>₹{taxAmount.toFixed(2)}</span></div>
          <div className="flex justify-between text-sm mb-2"><span>Shipping</span><span className="text-secondary">Free</span></div>
          <div className="border-t border-border my-3" />
          <div className="flex justify-between font-display text-2xl mb-4"><span>Total</span><span>₹{grandTotal.toFixed(2)}</span></div>

          <div className="mb-5" data-testid="checkout-coupon">
            {offer ? (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="checkout-savings">
                <div className="flex items-center justify-between">
                  <span className="text-xs inline-flex items-center gap-1.5">
                    <Tag size={11} className="text-primary" />
                    Offer <span className="font-medium">{offer.code}</span> applied
                  </span>
                  <button onClick={removeCode} className="text-xs text-muted-foreground hover:text-destructive" data-testid="coupon-remove">
                    Remove
                  </button>
                </div>
                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Original price</span>
                    <span className="line-through" data-testid="savings-original">₹{fullTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-primary">
                    <span>You save</span>
                    <span data-testid="savings-amount">− ₹{saved.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-medium border-t border-primary/20 pt-1.5">
                    <span>You pay</span>
                    <span data-testid="savings-final">₹{grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <Input
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                    placeholder="Offer code"
                    className="h-10 bg-transparent"
                    data-testid="coupon-input"
                  />
                  <Button variant="outline" onClick={() => applyCode()} disabled={checkingCode || !codeInput} className="btn-pill h-10" data-testid="coupon-apply">
                    {checkingCode ? "Checking…" : "Apply"}
                  </Button>
                </div>

                {offers.length > 0 && (
                  <div className="mt-3" data-testid="checkout-offer-list">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                      Offers running now
                    </div>
                    <div className="space-y-2">
                      {offers.map((o) => {
                        const short = subtotal < o.min_order;
                        return (
                          <button
                            key={o.code}
                            onClick={() => applyCode(o.code)}
                            disabled={checkingCode || short}
                            className="w-full text-left rounded-xl border border-dashed border-secondary/50 bg-secondary/5 px-3 py-2.5 hover:bg-secondary/10 disabled:opacity-60 disabled:hover:bg-secondary/5 transition-colors"
                            data-testid={`checkout-offer-${o.code}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-medium tracking-[0.14em] text-secondary">{o.code}</span>
                              <span className="text-[11px] text-primary">{short ? "Not eligible yet" : "Apply"}</span>
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-1">
                              {short
                                ? `${o.benefit} — add ₹${(o.min_order - subtotal).toFixed(2)} more`
                                : o.benefit}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {failed && (
            <div className="border border-destructive/30 bg-destructive/5 rounded-xl p-4 mb-4" data-testid="payment-failed-nudge">
              <div className="flex items-start gap-2 text-destructive text-sm font-medium">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>Payment not completed</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                {failed.reason} Your order <span className="font-mono">#{failed.orderId.slice(0, 8)}</span> is saved — finish it here or let us take over on WhatsApp.
              </p>
              <div className="flex flex-col gap-2 mt-3">
                <Button onClick={retryPayment} disabled={busy} className="btn-pill h-10 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="retry-payment-btn">
                  <RefreshCw size={14} className="mr-2" /> Retry Payment
                </Button>
                <Button onClick={retryOnWhatsapp} variant="outline" className="btn-pill h-10 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10" data-testid="retry-whatsapp-btn">
                  <MessageCircle size={14} className="mr-2" /> Retry on WhatsApp
                </Button>
              </div>
            </div>
          )}

          <Button onClick={pay} disabled={busy} className="w-full btn-pill bg-primary text-primary-foreground hover:bg-primary/90 h-11 mb-2" data-testid="checkout-pay-btn">
            {busy ? "Processing…" : "Pay Online"}
          </Button>
          <Button onClick={whatsappOrder} variant="outline" className="w-full btn-pill h-11 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10" data-testid="checkout-whatsapp-btn">
            <MessageCircle size={16} className="mr-2" /> Order on WhatsApp
          </Button>
          <p className="text-[11px] text-muted-foreground mt-3 text-center">Secured by Razorpay · UPI, Card & Netbanking</p>
        </div>
      </div>
    </main>
  );
}
